// Per-bot detail page (`/bots/:id`).
//
// Public profile for one AI pilot in the Meet the AI roster. Surfaces:
//   - Identity: handle, tier (Apex/Elite/Veteran/Rookie), base-strength bar
//   - Career: W/L, win-rate, total matches
//   - Head-to-head (auth only): viewer's record vs this bot + Revenge button
//   - Recent matches feed (last 10 across all brackets)
//   - Signature levels chips (top 3 most-played, links to /play/N)
//   - Catchphrases panel (sample win + loss lines)
//
// Linked from:
//   - `/tournaments` Meet the AI Bot Career Leaderboard rows
//   - bot handles inside `TournamentDetail` participant strip & matches
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Bot, Swords, Trophy, Skull, Target, Sparkles, Flame } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import api from "../lib/api";

// Tier → accent colour. Mirrors the strength-bar gradient on the leaderboard
// so a single hue identifies each tier across the app.
const TIER_ACCENT = {
  Apex: "#ffd700",
  Elite: "#b026ff",
  Veteran: "#00ffff",
  Rookie: "#7cffb0",
};

function StatCard({ label, value, accent = "#ffffff", testId }) {
  return (
    <div
      className="border border-white/10 bg-black/30 px-4 py-3"
      data-testid={testId}
    >
      <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/45 mb-1">
        {label}
      </div>
      <div
        className="font-heading text-2xl tabular-nums leading-none"
        style={{ color: accent }}
      >
        {value}
      </div>
    </div>
  );
}

export default function BotDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [revengeBusy, setRevengeBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/bots/${encodeURIComponent(id)}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(false); });
    return () => { cancelled = true; };
  }, [id]);

  const launchRevenge = async () => {
    if (revengeBusy || !data) return;
    setRevengeBusy(true);
    try {
      const r = await api.post("/bots/revenge-match", { bot_id: data.user_id, level: 1 });
      toast.success(`Revenge match queued · vs ${r.data?.bot_name || data.name}`, {
        icon: <Swords className="w-4 h-4 text-[#ffd700]" />,
        duration: 4000,
      });
      navigate(`/tournaments/${r.data?.tournament_id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't start revenge match";
      toast.error(msg, { duration: 4500 });
    } finally {
      setRevengeBusy(false);
    }
  };

  if (data === null) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" />
        <Navbar />
        <main className="relative z-10 flex items-center justify-center min-h-screen font-mono-cosmic text-[#8b949e] tracking-widest text-xs uppercase">
          Loading bot dossier…
        </main>
      </div>
    );
  }
  if (data === false) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" />
        <Navbar />
        <main className="relative z-10 max-w-3xl mx-auto px-6 pt-24 pb-16" data-testid="bot-not-found">
          <Link
            to="/tournaments"
            className="inline-flex items-center gap-2 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#00ffff]/70 hover:text-[#00ffff] mb-8"
          >
            <ArrowLeft className="w-3 h-3" /> Back to tournaments
          </Link>
          <div className="border border-[#ff007f]/40 bg-[#ff007f]/5 p-6 text-center">
            <Bot className="w-10 h-10 mx-auto mb-3 text-[#ff007f]/70" />
            <div className="font-heading text-2xl mb-1">Unknown bot</div>
            <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/45">
              No such pilot in the AI roster.
            </div>
          </div>
        </main>
      </div>
    );
  }

  const tierColor = TIER_ACCENT[data.tier] || "#ffffff";
  const rate = Math.round((data.career.win_rate || 0) * 100);

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main className="relative z-10 max-w-5xl mx-auto px-6 pt-24 pb-20" data-testid="bot-detail-page">
        <Link
          to="/tournaments"
          className="inline-flex items-center gap-2 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#00ffff]/70 hover:text-[#00ffff] mb-6"
          data-testid="bot-detail-back-link"
        >
          <ArrowLeft className="w-3 h-3" /> Back to roster
        </Link>

        {/* Hero — identity + tier + strength bar */}
        <section
          className="border bg-black/30 p-6 mb-8"
          style={{ borderColor: `${tierColor}40` }}
          data-testid="bot-detail-hero"
        >
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 grid place-items-center border"
                style={{ borderColor: `${tierColor}80`, background: `${tierColor}15` }}
              >
                <Bot className="w-8 h-8" style={{ color: tierColor }} />
              </div>
              <div>
                <div
                  className="font-mono-cosmic text-[10px] tracking-[0.4em] uppercase mb-1"
                  style={{ color: tierColor }}
                >
                  // {data.tier} Tier
                </div>
                <h1 className="font-heading text-4xl tracking-tight uppercase mb-1 text-white">
                  {data.name}
                </h1>
                <div className="font-mono-cosmic text-xs tracking-[0.25em] uppercase text-white/40">
                  {data.handle}
                </div>
              </div>
            </div>
            <div className="min-w-[200px]">
              <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/45 mb-1">
                Base Strength
              </div>
              <div className="font-heading text-3xl tabular-nums" style={{ color: tierColor }}>
                {(data.base_strength * 100).toFixed(0)}
                <span className="text-white/30 text-base">/100</span>
              </div>
              <div className="mt-2 h-1.5 border border-white/15 bg-black/50 relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0"
                  style={{
                    width: `${Math.min(100, data.base_strength * 100)}%`,
                    background: tierColor,
                    boxShadow: `0 0 8px ${tierColor}`,
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Career stats */}
        <section className="mb-8" data-testid="bot-detail-career">
          <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#b026ff]/80 mb-2">
            // Career
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Wins" value={data.career.wins} accent="#28e070" testId="bot-stat-wins" />
            <StatCard label="Losses" value={data.career.losses} accent="#ff5577" testId="bot-stat-losses" />
            <StatCard label="Win Rate" value={`${rate}%`} accent={tierColor} testId="bot-stat-rate" />
            <StatCard label="Total Matches" value={data.career.matches_total} accent="#ffffff" testId="bot-stat-total" />
          </div>
        </section>

        {/* Head-to-head vs viewer (auth only) */}
        {data.h2h && (data.h2h.wins + data.h2h.losses > 0) && (
          <section className="mb-8" data-testid="bot-detail-h2h">
            <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#ffd700]/80 mb-2">
              // You vs {data.name}
            </div>
            <div
              className="border bg-black/30 p-5 flex items-center justify-between gap-4 flex-wrap"
              style={{ borderColor: "#ffd70040" }}
            >
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/45 mb-1">
                    Your Wins
                  </div>
                  <div className="font-heading text-3xl tabular-nums text-[#28e070]">
                    {data.h2h.wins}
                  </div>
                </div>
                <div className="font-heading text-2xl text-white/30">–</div>
                <div className="text-center">
                  <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/45 mb-1">
                    Your Losses
                  </div>
                  <div className="font-heading text-3xl tabular-nums text-[#ff5577]">
                    {data.h2h.losses}
                  </div>
                </div>
                {/* Avenged chip — surfaces only when the viewer has at
                    least one revenge-bracket win against this bot. Goes
                    orange-flame when net-positive in the rivalry so it
                    reads as a victory lap rather than a stat. */}
                {data.h2h.avenged > 0 && (() => {
                  const netPositive = data.h2h.wins > data.h2h.losses;
                  const accent = netPositive ? "#ff8c1a" : "#ffd700";
                  return (
                    <div
                      className="hidden md:flex flex-col items-center pl-4 ml-2 border-l border-white/10"
                      data-testid="bot-detail-avenged"
                    >
                      <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/45 mb-1 flex items-center gap-1">
                        <Flame className="w-3 h-3" style={{ color: accent }} />
                        Avenged
                      </div>
                      <div
                        className="font-heading text-3xl tabular-nums"
                        style={{
                          color: accent,
                          textShadow: netPositive ? `0 0 12px ${accent}` : "none",
                        }}
                      >
                        {data.h2h.avenged}
                      </div>
                    </div>
                  );
                })()}
                {data.h2h.last_outcome && (
                  <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/55 hidden md:block">
                    Last:{" "}
                    <span style={{ color: data.h2h.last_outcome === "win" ? "#28e070" : "#ff5577" }}>
                      {data.h2h.last_outcome === "win" ? "Win" : "Loss"}
                    </span>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={launchRevenge}
                disabled={revengeBusy}
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#ffd700] text-[#ffd700] hover:bg-[#ffd700]/10 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase disabled:opacity-40 disabled:cursor-not-allowed transition"
                data-testid="bot-detail-revenge-btn"
              >
                <Swords className="w-3.5 h-3.5" />
                {revengeBusy ? "Queuing…" : "Revenge Match · 5 ☾"}
              </button>
            </div>
          </section>
        )}

        {/* Signature levels */}
        {data.signature_levels?.length > 0 && (
          <section className="mb-8" data-testid="bot-detail-signature-levels">
            <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#00ffff]/80 mb-2">
              // Signature Levels
            </div>
            <div className="flex flex-wrap gap-2">
              {data.signature_levels.map((sl) => (
                <Link
                  key={sl.level}
                  to={`/play/${sl.level}`}
                  className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#00ffff]/40 text-[#00ffff] hover:bg-[#00ffff]/10 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition"
                  data-testid={`bot-signature-level-${sl.level}`}
                >
                  <Target className="w-3 h-3" />
                  Level {sl.level}
                  <span className="text-white/40">× {sl.appearances}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Catchphrases */}
        {(data.catchphrases?.win?.length || data.catchphrases?.loss?.length) ? (
          <section className="mb-8" data-testid="bot-detail-catchphrases">
            <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#b026ff]/80 mb-2">
              // Catchphrases
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border border-[#28e070]/30 bg-black/30 p-4">
                <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#28e070] mb-2 flex items-center gap-1.5">
                  <Trophy className="w-3 h-3" /> On Victory
                </div>
                <ul className="space-y-1.5">
                  {(data.catchphrases.win || []).map((line, i) => (
                    <li
                      key={i}
                      className="font-mono-cosmic text-xs leading-snug text-white/85 italic"
                      data-testid={`bot-quote-win-${i}`}
                    >
                      "{line}"
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border border-[#ff5577]/30 bg-black/30 p-4">
                <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#ff5577] mb-2 flex items-center gap-1.5">
                  <Skull className="w-3 h-3" /> On Defeat
                </div>
                <ul className="space-y-1.5">
                  {(data.catchphrases.loss || []).map((line, i) => (
                    <li
                      key={i}
                      className="font-mono-cosmic text-xs leading-snug text-white/85 italic"
                      data-testid={`bot-quote-loss-${i}`}
                    >
                      "{line}"
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        ) : null}

        {/* Recent matches */}
        <section data-testid="bot-detail-recent-matches">
          <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/55 mb-2">
            // Recent Matches
          </div>
          {data.recent_matches?.length ? (
            <div className="border border-white/10 bg-black/30 divide-y divide-white/5">
              {data.recent_matches.map((m, i) => (
                <Link
                  key={`${m.tournament_id}-${i}`}
                  to={`/tournaments/${m.tournament_id}`}
                  className="grid grid-cols-[auto_1fr_auto] gap-3 px-3 py-2 hover:bg-white/5 transition items-center"
                  data-testid={`bot-match-row-${i}`}
                >
                  <span
                    className={`inline-block px-1.5 py-0.5 font-mono-cosmic text-[8px] tracking-[0.25em] uppercase border ${
                      m.outcome === "win"
                        ? "border-[#28e070]/50 text-[#28e070]"
                        : "border-[#ff5577]/50 text-[#ff5577]"
                    }`}
                  >
                    {m.outcome}
                  </span>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono-cosmic text-[11px] text-white/85 truncate">
                      vs{" "}
                      {m.opponent_is_bot && m.opponent_id ? (
                        <Link
                          to={`/bots/${encodeURIComponent(m.opponent_id)}`}
                          className="text-[#b026ff] hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {m.opponent_handle}
                        </Link>
                      ) : (
                        <span className="text-white/85">{m.opponent_handle}</span>
                      )}
                    </span>
                    <span className="font-mono-cosmic text-[9px] tracking-[0.2em] uppercase text-white/35 hidden sm:inline">
                      · Level {m.level} · R{m.round}
                    </span>
                  </div>
                  <span className="font-mono-cosmic text-[9px] tracking-widest text-white/40 tabular-nums">
                    {m.completed_at?.slice(0, 10) || "—"}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="border border-white/10 bg-black/30 px-4 py-6 text-center font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/45">
              No matches yet · this pilot is fresh out of the simulator
            </div>
          )}
          {data.recent_matches?.length > 0 && (
            <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/35 mt-2 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              Last {data.recent_matches.length} matches · click a row to view the bracket
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
